import React from 'react';
import { Box, Grid, Typography, Card, CardContent, ButtonGroup, Button } from '@mui/material';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getValidTenantId } from '../../utils/tenantUtils';

const TimeframeSelector = ({ timeframe, onChange }) => (
  <ButtonGroup size="small" aria-label="timeframe selection">
    <Button variant={timeframe === '1m' ? 'contained' : 'outlined'} onClick={() => onChange('1m')}>1M</Button>
    <Button variant={timeframe === '3m' ? 'contained' : 'outlined'} onClick={() => onChange('3m')}>3M</Button>
    <Button variant={timeframe === '6m' ? 'contained' : 'outlined'} onClick={() => onChange('6m')}>6M</Button>
    <Button variant={timeframe === '1y' ? 'contained' : 'outlined'} onClick={() => onChange('1y')}>1Y</Button>
  </ButtonGroup>
);

const FinancialAnalytics = ({ data, timeframe, onTimeframeChange }) => {
  // Extract data with fallbacks
  const revenue = data?.revenue || [];
  const transactions = data?.transactions || [];
  const summary = data?.summary || {};

  // Make sure we have tenant ID for the component context
  const tenantId = getValidTenantId();

  // Format currency based on locale
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Financial Analytics</Typography>
        <TimeframeSelector timeframe={timeframe} onChange={onTimeframeChange} />
      </Box>

      {/* Financial Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="textSecondary">Total Revenue</Typography>
              <Typography variant="h4">{formatCurrency(summary.totalRevenue || 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="textSecondary">Average Transaction</Typography>
              <Typography variant="h4">{formatCurrency(summary.averageTransaction || 0)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="textSecondary">Transaction Count</Typography>
              <Typography variant="h4">{summary.transactionCount || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="textSecondary">Growth</Typography>
              <Typography 
                variant="h4" 
                color={(summary.growth || 0) >= 0 ? 'success.main' : 'error.main'}
              >
                {(summary.growth || 0) >= 0 ? '+' : ''}{(summary.growth || 0).toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Revenue Chart */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Revenue Over Time</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenue} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#8884d8" 
                    activeDot={{ r: 8 }}
                    name="Revenue"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Transaction Count Chart */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Transactions by Month</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={transactions} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" name="Transactions" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinancialAnalytics;
